import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { XIcon, CheckIcon } from 'lucide-react';
interface FilterBarProps {
  locations: any[];
  trainingTypes: any[];
  currentFilters: any;
  onApplyFilters: (filters: any) => void;
  onCancel: () => void;
}
export function FilterBar({
  locations,
  trainingTypes,
  currentFilters,
  onApplyFilters,
  onCancel
}: FilterBarProps) {
  const [filters, setFilters] = useState({
    locations: [...currentFilters.locations],
    trainingTypes: [...currentFilters.trainingTypes],
    dateRange: {
      ...currentFilters.dateRange
    }
  });
  // Handle location filter change
  const handleLocationChange = (locationId: number) => {
    if (filters.locations.includes(locationId)) {
      setFilters({
        ...filters,
        locations: filters.locations.filter(id => id !== locationId)
      });
    } else {
      setFilters({
        ...filters,
        locations: [...filters.locations, locationId]
      });
    }
  };
  // Handle training type filter change
  const handleTrainingTypeChange = (typeId: number) => {
    if (filters.trainingTypes.includes(typeId)) {
      setFilters({
        ...filters,
        trainingTypes: filters.trainingTypes.filter(id => id !== typeId)
      });
    } else {
      setFilters({
        ...filters,
        trainingTypes: [...filters.trainingTypes, typeId]
      });
    }
  };
  // Handle date range change
  const handleDateRangeChange = (field: string, value: string) => {
    setFilters({
      ...filters,
      dateRange: {
        ...filters.dateRange,
        [field]: new Date(value)
      }
    });
  };
  // Format date for input value
  const formatDateForInput = (date: Date) => {
    return date.toISOString().split('T')[0];
  };
  // Handle apply filters
  const handleApply = () => {
    onApplyFilters(filters);
  };
  // Select all locations
  const selectAllLocations = () => {
    setFilters({
      ...filters,
      locations: locations.map(loc => loc.id)
    });
  };
  // Select all training types
  const selectAllTrainingTypes = () => {
    setFilters({
      ...filters,
      trainingTypes: trainingTypes.map(type => type.id)
    });
  };
  return <div className="space-y-4" data-id="element-3036">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3037">
        {/* Locations filter */}
        <div data-id="element-3038">
          <div className="flex justify-between items-center mb-2" data-id="element-3039">
            <h3 className="text-sm font-medium text-gray-700" data-id="element-3040">Locaties</h3>
            <button className="text-xs text-[#B61C1C] hover:text-[#961818]" onClick={selectAllLocations} data-id="element-3041">
              Selecteer alles
            </button>
          </div>
          <div className="space-y-2" data-id="element-3042">
            {locations.map(location => <div key={location.id} className="flex items-center" data-id="element-3043">
                <input type="checkbox" id={`location-${location.id}`} checked={filters.locations.includes(location.id)} onChange={() => handleLocationChange(location.id)} className="h-4 w-4 text-[#B61C1C] focus:ring-[#B61C1C] border-gray-300 rounded" data-id="element-3044" />
                <label htmlFor={`location-${location.id}`} className="ml-2 text-sm text-gray-700" data-id="element-3045">
                  {location.name}
                </label>
              </div>)}
          </div>
        </div>
        {/* Training types filter */}
        <div data-id="element-3046">
          <div className="flex justify-between items-center mb-2" data-id="element-3047">
            <h3 className="text-sm font-medium text-gray-700" data-id="element-3048">
              Training Types
            </h3>
            <button className="text-xs text-[#B61C1C] hover:text-[#961818]" onClick={selectAllTrainingTypes} data-id="element-3049">
              Selecteer alles
            </button>
          </div>
          <div className="space-y-2" data-id="element-3050">
            {trainingTypes.map(type => <div key={type.id} className="flex items-center" data-id="element-3051">
                <input type="checkbox" id={`type-${type.id}`} checked={filters.trainingTypes.includes(type.id)} onChange={() => handleTrainingTypeChange(type.id)} className="h-4 w-4 text-[#B61C1C] focus:ring-[#B61C1C] border-gray-300 rounded" data-id="element-3052" />
                <label htmlFor={`type-${type.id}`} className="ml-2 text-sm text-gray-700 flex items-center" data-id="element-3053">
                  <span className={`inline-block w-3 h-3 rounded-full mr-1 bg-${type.color}-500`} data-id="element-3054"></span>
                  {type.name}
                </label>
              </div>)}
          </div>
        </div>
        {/* Date range filter */}
        <div data-id="element-3055">
          <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-3056">
            Datum Bereik
          </h3>
          <div className="space-y-3" data-id="element-3057">
            <div data-id="element-3058">
              <label htmlFor="start-date" className="block text-xs text-gray-500 mb-1" data-id="element-3059">
                Startdatum
              </label>
              <input type="date" id="start-date" value={formatDateForInput(filters.dateRange.start)} onChange={e => handleDateRangeChange('start', e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#B61C1C] focus:border-[#B61C1C] sm:text-sm" data-id="element-3060" />
            </div>
            <div data-id="element-3061">
              <label htmlFor="end-date" className="block text-xs text-gray-500 mb-1" data-id="element-3062">
                Einddatum
              </label>
              <input type="date" id="end-date" value={formatDateForInput(filters.dateRange.end)} onChange={e => handleDateRangeChange('end', e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#B61C1C] focus:border-[#B61C1C] sm:text-sm" data-id="element-3063" />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end space-x-2 pt-2 border-t border-gray-200" data-id="element-3064">
        <Button variant="outline" size="sm" onClick={onCancel} data-id="element-3065">
          <XIcon className="h-4 w-4 mr-1" data-id="element-3066" />
          Annuleren
        </Button>
        <Button variant="primary" size="sm" onClick={handleApply} data-id="element-3067">
          <CheckIcon className="h-4 w-4 mr-1" data-id="element-3068" />
          Filters Toepassen
        </Button>
      </div>
    </div>;
}